import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

interface IParams {
  isEditing: boolean;
  projects: any[];
  notes: any[];
  projectOrderList: any[];
  setProjects: (projects: any[]) => void;
  setNotes: (notes: any[]) => void;
  setProjectOrderList: (orders: any[]) => void;
}

interface IAutoSaveData {
  projects: any[];
  notes: any[];
  projectOrderList: any[];
  baselineProjects: any[];
  baselineNotes: any[];
  baselineProjectOrderList: any[];
}

const EMPTY_DATA: IAutoSaveData = {
  projects: [],
  notes: [],
  projectOrderList: [],
  baselineProjects: [],
  baselineNotes: [],
  baselineProjectOrderList: [],
};

export const useInDevProjectsAllAutoSave = ({
  isEditing,
  projects,
  notes,
  projectOrderList,
  setProjects,
  setNotes,
  setProjectOrderList,
}: IParams) => {
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [baselineProjects, setBaselineProjects] = useState<any[]>([]);
  const [baselineNotes, setBaselineNotes] = useState<any[]>([]);
  const [baselineProjectOrderList, setBaselineProjectOrderList] = useState<any[]>([]);

  const autoSavePageKey = EDIT_PAGE_KEY_MAP.studio_indev_all;
  const initialParamsRef = useRef<IAutoSaveData>(deepClone(EMPTY_DATA));

  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: autoSavePageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey,
  } = autoSaveMethods;

  const captureBaseline = useCallback((projectList: any[], noteList: any[], orderList: any[]) => {
    const clonedProjects = deepClone(projectList || []);
    const clonedNotes = deepClone(noteList || []);
    const clonedOrders = deepClone(orderList || []);

    setBaselineProjects(clonedProjects);
    setBaselineNotes(clonedNotes);
    setBaselineProjectOrderList(clonedOrders);

    initialParamsRef.current = {
      projects: clonedProjects,
      notes: clonedNotes,
      projectOrderList: clonedOrders,
      baselineProjects: clonedProjects,
      baselineNotes: clonedNotes,
      baselineProjectOrderList: clonedOrders,
    };
  }, []);

  const clearBaseline = useCallback(() => {
    setBaselineProjects([]);
    setBaselineNotes([]);
    setBaselineProjectOrderList([]);
    initialParamsRef.current = deepClone(EMPTY_DATA);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;
    const hasProjectChanges = JSON.stringify(projects || []) !== JSON.stringify(baselineProjects || []);
    const hasNoteChanges = JSON.stringify(notes || []) !== JSON.stringify(baselineNotes || []);
    const hasOrderChanges =
      JSON.stringify(projectOrderList || []) !== JSON.stringify(baselineProjectOrderList || []);
    return hasProjectChanges || hasNoteChanges || hasOrderChanges;
  }, [
    isEditing,
    projects,
    notes,
    projectOrderList,
    baselineProjects,
    baselineNotes,
    baselineProjectOrderList,
  ]);

  const currentSaveParams = useMemo(() => {
    if (!isEditing) {
      return deepClone(EMPTY_DATA);
    }
    return {
      projects: projects || [],
      notes: notes || [],
      projectOrderList: projectOrderList || [],
      baselineProjects: baselineProjects || [],
      baselineNotes: baselineNotes || [],
      baselineProjectOrderList: baselineProjectOrderList || [],
    };
  }, [
    isEditing,
    projects,
    notes,
    projectOrderList,
    baselineProjects,
    baselineNotes,
    baselineProjectOrderList,
  ]);

  useAutoSaveParams({
    pageKey: autoSavePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
  });

  useEffect(() => {
    if (!isEditing) {
      initialParamsRef.current = deepClone(EMPTY_DATA);
    }
  }, [isEditing]);

  const handleEditWithAutoSave = useCallback((onNoRestore: () => void) => {
    if (showRestorePrompt(autoSavePageKey)) {
      setAutoSaveVisible(true);
      return;
    }
    onNoRestore();
  }, [showRestorePrompt, autoSavePageKey]);

  const handleAutoSaveConfirm = useCallback(() => {
    const savedData = (getSaveParamsValue(autoSavePageKey) || {}) as IAutoSaveData;
    setAutoSaveVisible(false);

    if (Array.isArray(savedData.projects)) {
      setProjects(savedData.projects);
    }
    if (Array.isArray(savedData.notes)) {
      setNotes(savedData.notes);
    }
    if (Array.isArray(savedData.projectOrderList)) {
      setProjectOrderList(savedData.projectOrderList);
    }

    const restoredBaselineProjects = savedData.baselineProjects || savedData.projects || [];
    const restoredBaselineNotes = savedData.baselineNotes || savedData.notes || [];
    const restoredBaselineOrders = savedData.baselineProjectOrderList || savedData.projectOrderList || [];

    setBaselineProjects(restoredBaselineProjects);
    setBaselineNotes(restoredBaselineNotes);
    setBaselineProjectOrderList(restoredBaselineOrders);

    initialParamsRef.current = {
      projects: savedData.projects || [],
      notes: savedData.notes || [],
      projectOrderList: savedData.projectOrderList || [],
      baselineProjects: restoredBaselineProjects,
      baselineNotes: restoredBaselineNotes,
      baselineProjectOrderList: restoredBaselineOrders,
    };

    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [
    getSaveParamsValue,
    autoSavePageKey,
    setProjects,
    setNotes,
    setProjectOrderList,
    deleteSaveParamsByPageKey,
  ]);

  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  const clearAutoSave = useCallback(() => {
    clearBaseline();
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [clearBaseline, deleteSaveParamsByPageKey, autoSavePageKey]);

  return {
    autoSaveVisible,
    hasUnsavedChanges,
    captureBaseline,
    clearBaseline,
    handleEditWithAutoSave,
    handleAutoSaveConfirm,
    handleAutoSaveCancel,
    clearAutoSave,
  };
};

